import React from 'react'
import { Button, Space, Typography, theme, Row, Col, Flex } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { useFetchQuestionsQuery } from '../../../../../../../store/apis/question.api'
import { useParams } from 'react-router-dom'
import './styles.scss'
import CategoryCard from './CategoryCard'
import OtherCategorySelector from './OtherCategorySelector'
import BatchIndicator from './BatchIndicator'
import { useVersionId } from '../../../../../../../utils/parseURL.helper'
import { useRefinementFlow } from '../hooks/useRefinementFlow'

const { Title } = Typography

/**
 * Component that renders a category choice message for a submission
 */
const CategoryChoiceMessage = ({ refinementItem }) => {
    const { isItemEditable, handleSkip } = useRefinementFlow()
    const isSkipped = Boolean(refinementItem?.isSkipped)
    const isDisabled = !isItemEditable(refinementItem?.id)
    const { token: themeToken } = theme.useToken()
    const { token } = useParams()
    const versionId = useVersionId()

    const { data: questions } = useFetchQuestionsQuery({ token, versionId })

    const onSkip = () => handleSkip(refinementItem.id)

    // Check if an "other" category (not one of the first two) has been selected
    const selectedCategoryId = refinementItem?.selectedCategoryId
    const displayedCategoryIds = [
        refinementItem?.categories?.[0]?.id,
        refinementItem?.categories?.[1]?.id
    ].filter(Boolean)
    const hasChosenOtherCategory = selectedCategoryId && !displayedCategoryIds.includes(selectedCategoryId)

    // If the refinementItem doesn't have the required data, don't render
    if (!refinementItem.categories?.length || refinementItem.categories.length < 2) {
        return null
    }

    return (
        <div className='category-choice-message'>
            <Space direction='vertical' style={{ width: '100%' }}>
                <Flex justify='space-between' align='top'>
                    <Title
                        level={5}
                        className='category-choice__title'
                        style={{ color: themeToken.colorTextSecondary }}
                    >
                        For the submission displayed, I've identified two potentially applicable categories:
                    </Title>
                    <BatchIndicator refinementItemId={refinementItem.id} />
                </Flex>
                <Row gutter={16}>
                    <Col span={12}>
                        <CategoryCard
                            category={{
                                id: refinementItem.categories[0].id,
                                description: refinementItem.categories[0].description,
                                explanation: refinementItem.categories[0].explainability?.reasoning || '',
                                isAdequate: refinementItem.categories[0].explainability?.isAdequate,
                                tags: refinementItem.categories[0].tags,
                            }}
                            refinementItemId={refinementItem.id}
                            isDisabled={isDisabled}
                            questions={questions}
                        />
                    </Col>
                    <Col span={12}>
                        <CategoryCard
                            category={{
                                id: refinementItem.categories[1].id,
                                description: refinementItem.categories[1].description,
                                explanation: refinementItem.categories[1].explainability?.reasoning || '',
                                isAdequate: refinementItem.categories[1].explainability?.isAdequate,
                                tags: refinementItem.categories[1].tags,
                            }}
                            refinementItemId={refinementItem.id}
                            isDisabled={isDisabled}
                            questions={questions}
                        />
                    </Col>
                </Row>

                <Flex justify='space-between'>
                    <OtherCategorySelector
                        refinementItem={refinementItem}
                        questions={questions}
                        isDisabled={isDisabled}
                        selectedCategoryId={selectedCategoryId}
                        hasChosen={hasChosenOtherCategory}
                    />
                    <Button
                        onClick={onSkip}
                        type={isSkipped ? 'text' : 'text'}
                        disabled={isDisabled}
                        icon={!isSkipped && <QuestionCircleOutlined />}
                        className='category-choice__skip-button'
                        style={
                            isSkipped
                                ? {
                                      backgroundColor: themeToken.colorErrorBg,
                                      borderColor: themeToken.colorError,
                                      color: themeToken.colorError,
                                      fontWeight: 500,
                                  }
                                : {}
                        }
                    >
                        {isSkipped ? 'Skipped' : "I'm not sure"}
                    </Button>
                </Flex>
            </Space>
        </div>
    )
}

export default CategoryChoiceMessage
